import React, { useState } from 'react';
import { AlertTriangle, Target, Rocket } from 'lucide-react';
import ChampionSelect from './ChampionSelect';
import Modal from '../design-system/Modal';
import Button from '../design-system/Button';
import { Input, Textarea } from '../design-system';

const FOCUS_TEMPLATES = [
  '🎯 Réduire morts greed à 0','🛡️ Amélioration positionning TF','⚡ Dmg/min cible 900+',
  '🎮 Lane dominance early (plates + kill pressure)','🧠 Macro : wave management parfait',
  '🤝 Follow-up jungler systématique','💀 Zéro overstay bot après 12min','📊 CS/min cible 9+',
];

export default function PreGameModal({ onClose, onSave, champions, version }) {
  const [mental, setMental] = useState(7);
  const [focusArea, setFocusArea] = useState('');
  const [customFocus, setCustomFocus] = useState('');
  const [intendedPick, setIntendedPick] = useState('Caitlyn');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const effectiveFocus = customFocus.trim() || focusArea;
  const shouldWarn = mental < 6;

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ mental_pre_queue: mental, focus_area: effectiveFocus || null, intended_pick: intendedPick || null, notes: notes.trim() || null });
    } catch (e) {
      alert('Erreur : ' + e.message);
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Prépare ta prochaine game"
      subtitle={<span className="flex items-center gap-1"><Rocket size={10} /> Pre-Queue Check</span>}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>ANNULER</Button>
          <Button variant="primary" onClick={save} disabled={saving} loading={saving} className="flex-1">
            {saving ? 'SAUVEGARDE...' : 'LOCK INTENT & QUEUE'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Mental slider */}
        <div>
          <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-2 font-semibold">Mental avant la queue (1-10)</div>
          <input type="range" min="1" max="10" value={mental} onChange={(e) => setMental(+e.target.value)} className="w-full accent-cyan-400" />
          <div className="flex items-center justify-between text-[10px] text-zinc-600 font-mono mt-1">
            <span>Tilté / Fatigué</span>
            <span className="text-xl font-black bg-gradient-to-r from-cyan-400 to-fuchsia-400 bg-clip-text text-transparent">{mental}/10</span>
            <span>Focus parfait</span>
          </div>
          {shouldWarn && (
            <div className="mt-3 border-2 border-amber-400 bg-amber-400/10 p-3 flex items-start gap-3">
              <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-black text-amber-400 tracking-wider mb-1">MENTAL BAS DÉTECTÉ</div>
                <div className="text-zinc-300 text-xs">Tu es à {mental}/10. Considère : normal games d'abord, 10 min break, ou skip cette session.</div>
              </div>
            </div>
          )}
        </div>

        {/* Focus area */}
        <div>
          <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-2 font-semibold"><Target size={10} className="inline mr-1" /> Focus de la game</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {FOCUS_TEMPLATES.map(t => (
              <button key={t} onClick={() => { setFocusArea(t); setCustomFocus(''); }}
                className={`p-2 border text-xs text-left transition ${focusArea === t && !customFocus ? 'border-cyan-400 bg-cyan-400/10 text-white' : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'}`}>
                {t}
              </button>
            ))}
          </div>
          <Input value={customFocus} onChange={(e) => { setCustomFocus(e.target.value); setFocusArea(''); }} placeholder="Ou tape un focus custom..." />
        </div>

        <ChampionSelect label="Pick Envisagé" value={intendedPick} onChange={setIntendedPick} champions={champions} version={version} accent="cyan" />

        <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Ex: je viens de perdre 2 games, je vais jouer safe..." rows={3} />

        {/* Summary */}
        <div className="border border-zinc-800 bg-zinc-900/50 p-3 text-xs font-mono">
          <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Récap de ton intent</div>
          <div className="space-y-1 text-zinc-300">
            <div>Mental: <span className={mental >= 7 ? 'text-cyan-400' : mental >= 5 ? 'text-amber-400' : 'text-rose-400'}>{mental}/10</span></div>
            <div>Focus: <span className="text-white">{effectiveFocus || '—'}</span></div>
            <div>Pick: <span className="text-white">{intendedPick || '—'}</span></div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
